import { useEffect, useState } from 'react';
import quoteServices from '../../../../services/oneButtonServices/quoteServices';
import { useOneButtonStore } from '../../../../store/oneButtonStore';

export type FinanceAgreementMilestoneSummary = {
    loading: boolean;
    hasSigned: boolean;
    complete: boolean;
    sublabel: string;
    signedPartnerLabels: string[];
    quoteContracts?: Record<string, unknown>[];
};

function financePartnerKey(name: string): string {
    return String(name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '');
}

/** AgreementsModal `parseIsContractSignedRaw`. */
function parseIsContractSignedRaw(c: Record<string, unknown>): boolean {
    const v = c.IsContractSigned ?? c.isContractSigned;
    if (v === true || v === 'true' || v === 1 || v === '1') return true;
    if (v === false || v === 'false' || v === 0 || v === '0') return false;
    if (v == null || v === '') return false;
    if (typeof v === 'string') {
        const s = v.trim().toLowerCase();
        if (s === 'yes' || s === 'signed') return true;
        if (s === 'no' || s === 'unsigned' || s === 'in-progress' || s === 'in_progress') return false;
    }
    return false;
}

/** AgreementsModal `isSignFinancePartnerContractColumnSigned` on raw contract rows. */
export function isQuoteContractRowSigned(row: Record<string, unknown>): boolean {
    const contractStatus = String(
        row.Opportunity_Contract_Status ?? row.contractStatus ?? row.TrinityContractStatus ?? '',
    )
        .trim()
        .toLowerCase();
    return parseIsContractSignedRaw(row) || contractStatus === 'signed';
}

function formatPartnerLabel(raw: string): string {
    const t = String(raw || '').trim();
    if (!t) return t;
    if (financePartnerKey(t).includes('goodleap')) return 'Good Leap';
    if (financePartnerKey(t).includes('lightreach')) return 'Lightreach';
    return t;
}

function addPartnerKey(keys: Set<string>, raw: string) {
    const trimmed = String(raw || '').trim();
    if (!trimmed) return;
    if (trimmed.toLowerCase().includes('select finance')) return;
    keys.add(financePartnerKey(trimmed));
}

function collectActiveFinancePartnerKeys(
    opportunityDetails: unknown,
    selectedFinanceCells: Set<string> | null | undefined,
): Set<string> {
    const keys = new Set<string>();
    const od = opportunityDetails as Record<string, unknown> | null | undefined;
    const nested = (od?.OpportunityDetails ?? od?.opportunityDetails) as Record<string, unknown> | undefined;
    const products = (od?.OpportunityProduct ??
        od?.opportunityProduct ??
        nested?.OpportunityProduct ??
        nested?.opportunityProduct ??
        []) as unknown[];
    if (Array.isArray(products)) {
        products.forEach((p) => {
            const row = p as Record<string, unknown>;
            if (row.ActiveFlag === false || row.activeFlag === false) return;
            addPartnerKey(
                keys,
                String(
                    row.Finance_Partner_Display_Name ??
                        row.FinancePartnerDisplayName ??
                        row.Finance_Partner ??
                        row.finance_Partner ??
                        '',
                ),
            );
        });
    }
    const financeInfo = (od?.OpportunityProductsFinanceInfo ??
        od?.opportunityProductsFinanceInfo ??
        nested?.OpportunityProductsFinanceInfo) as unknown[] | undefined;
    if (Array.isArray(financeInfo)) {
        financeInfo.forEach((p) => {
            const row = p as Record<string, unknown>;
            addPartnerKey(
                keys,
                String(
                    row.Finance_Partner_Display_Name ??
                        row.FinancePartnerDisplayName ??
                        row.Finance_Partner ??
                        row.finance_Partner ??
                        '',
                ),
            );
        });
    }
    selectedFinanceCells?.forEach((cellKey) => {
        addPartnerKey(keys, cellKey.split('::')[0] ?? '');
    });
    const leadList = (od?.FinancePartnerLeadList ??
        od?.financePartnerLeadListModels ??
        od?.financePartnerLeadList) as unknown[] | undefined;
    if (Array.isArray(leadList)) {
        leadList.forEach((p) => {
            const row = p as Record<string, unknown>;
            addPartnerKey(keys, String(row.FinancePartnerName ?? row.Finance_Partner_Name ?? ''));
        });
    }
    return keys;
}

export function summarizeSignedFinanceAgreements(
    rawRows: unknown[],
    opportunityDetails: unknown,
    selectedFinanceCells: Set<string> | null | undefined,
): Omit<FinanceAgreementMilestoneSummary, 'loading'> {
    const activePartnerKeys = collectActiveFinancePartnerKeys(opportunityDetails, selectedFinanceCells);
    const signedPartners: string[] = [];
    const seen = new Set<string>();
    let activeContractCount = 0;
    let signedContractCount = 0;

    for (const raw of rawRows) {
        if (!raw || typeof raw !== 'object') continue;
        const row = raw as Record<string, unknown>;
        const partnerRaw = String(row.FinancePartner ?? row.financePartner ?? '').trim();
        if (!partnerRaw) continue;
        const key = financePartnerKey(partnerRaw);
        if (activePartnerKeys.size > 0 && !activePartnerKeys.has(key)) continue;
        activeContractCount++;
        const isSigned = isQuoteContractRowSigned(row);
        if (!isSigned) continue;
        signedContractCount++;
        if (seen.has(key)) continue;
        seen.add(key);
        signedPartners.push(formatPartnerLabel(partnerRaw));
    }

    const hasSigned = signedPartners.length > 0;
    let sublabel = 'View agreements';
    if (activeContractCount > 0 && signedContractCount === activeContractCount) {
        sublabel = 'Signed';
    } else if (signedContractCount > 0) {
        sublabel = 'Partially signed';
    } else if (rawRows.length > 0) {
        sublabel = 'Pending signature';
    }

    return {
        hasSigned,
        complete: hasSigned,
        sublabel,
        signedPartnerLabels: signedPartners,
        quoteContracts: rawRows.map((r) => r as Record<string, unknown>),
    };
}

export function useFinanceAgreementMilestone(
    opportunityNumericId: string | number | null | undefined,
): FinanceAgreementMilestoneSummary {
    const agreementsRefreshTrigger = useOneButtonStore((s) => s.agreementsRefreshTrigger);
    const opportunityDetails = useOneButtonStore((s) => s.opportunityDetails);
    const selectedFinanceCells = useOneButtonStore((s) => s.selectedFinanceCells);
    const [state, setState] = useState<FinanceAgreementMilestoneSummary>({
        loading: false,
        hasSigned: false,
        complete: false,
        sublabel: 'View agreements',
        signedPartnerLabels: [],
        quoteContracts: [],
    });

    useEffect(() => {
        if (!opportunityNumericId) {
            setState({
                loading: false,
                hasSigned: false,
                complete: false,
                sublabel: 'View agreements',
                signedPartnerLabels: [],
                quoteContracts: [],
            });
            return;
        }

        let cancelled = false;
        setState((prev) => ({ ...prev, loading: true }));

        void (async () => {
            try {
                const res = await quoteServices.retrieveContract(opportunityNumericId);
                if (cancelled) return;
                const qc = res?.QuoteContract ?? res?.quoteContract ?? [];
                const summary = summarizeSignedFinanceAgreements(
                    Array.isArray(qc) ? qc : [],
                    opportunityDetails,
                    selectedFinanceCells,
                );
                setState({ loading: false, ...summary });
            } catch {
                if (!cancelled) {
                    setState({
                        loading: false,
                        hasSigned: false,
                        complete: false,
                        sublabel: 'View agreements',
                        signedPartnerLabels: [],
                        quoteContracts: [],
                    });
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [
        opportunityNumericId,
        agreementsRefreshTrigger,
        opportunityDetails,
        selectedFinanceCells,
    ]);

    return state;
}
